import { useParams, Link } from 'react-router-dom';
import type { AreaKey, Basis } from '@exigo/shared';
import { emptyScenarioInput } from '@exigo/shared';
import { PageHeader } from '../../components/layout/PageHeader';
import { TextInput } from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { useReferenceQuery } from '../../api/queries';
import { useScenarioForm } from './useScenarioForm';
import { DealContextCard } from './DealContextCard';
import { CapabilityPanel } from './CapabilityPanel';
import { AreaTabs } from './AreaTabs';
import { AcrCard } from './AcrCard';
import { SeatsAcvCard } from './SeatsAcvCard';
import { PrequalCard } from './PrequalCard';
import { DealValueStrip } from './DealValueStrip';
import { ResultsList } from './ResultsList';
import { ClaimConstraints } from './ClaimConstraints';
import { GuideFooter } from './GuideFooter';
import { PromotionsCard } from './PromotionsCard';
import styles from './CalculatorPage.module.css';

export function CalculatorPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const reference = useReferenceQuery();
  const { record, input, setInput, result, isEvaluating, evaluateError, updateMeta } = useScenarioForm(id ?? '');

  if (record === undefined || reference.isLoading || !input) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
        <Spinner size={24} />
      </div>
    );
  }

  if (record === null) {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <EmptyState
          title="Scenario not found"
          body="It may have been deleted, or this browser session has ended."
          action={
            <Link to="/scenarios">
              <Button variant="primary">Back to scenarios</Button>
            </Link>
          }
        />
      </div>
    );
  }

  if (reference.isError || !reference.data) {
    return (
      <div style={{ padding: 'var(--space-8)' }}>
        <EmptyState title="Could not load reference data" body="Check the API is running and try again." />
      </div>
    );
  }

  // `ScenarioInput.area` is typed as the wider `AreaKey` (it also carries the
  // UI-only `promos` tab), but `basesByArea` only ever has entries for the four
  // areas AreaTabs can actually select — `promos` never reaches this input.
  const basesInArea: Basis[] =
    (reference.data.basesByArea as Partial<Record<AreaKey, Basis[]>>)[input.area] ?? [];
  const anyInput =
    input.workloads.some((w) => w.amount > 0) ||
    input.manualAcr.incremental > 0 ||
    input.seats > 0 ||
    input.acv > 0;

  const reference_ = reference.data;
  const isPromos = input.area === 'promos';

  /**
   * Clears every deal figure while keeping the capability register, matching the
   * legacy tool. The register is a standing fact about what Exigo holds, not a
   * number in this deal, so wiping it here would cost the user real re-entry for
   * no benefit. Deal context (segment, market, FX) is kept for the same reason.
   */
  const resetToZero = (): void => {
    const ok = window.confirm(
      'Clear every deal figure back to zero? Your capability register and deal context are kept.',
    );
    if (!ok) return;
    setInput((prev) => ({
      ...emptyScenarioInput(reference_.defaults),
      capability: prev.capability,
      area: prev.area,
      segment: prev.segment,
      market: prev.market,
      fx: prev.fx,
    }));
  };

  return (
    <>
      <PageHeader
        title={
          <div className={styles.nameRow}>
            <Link to="/scenarios" className={styles.backLink} aria-label="Back to scenarios">
              <BackIcon />
            </Link>
            <TextInput
              value={record.name}
              onChange={(e) => { updateMeta({ name: e.target.value }); }}
              aria-label="Scenario name"
              className={`${styles.headerInput} ${styles.headerInputName}`}
            />
          </div>
        }
        meta={
          <TextInput
            value={record.customerName ?? ''}
            onChange={(e) => { updateMeta({ customerName: e.target.value || null }); }}
            placeholder="Customer / opportunity name"
            aria-label="Customer name"
            className={styles.headerInput}
          />
        }
        actions={
          <div className={styles.headerActions}>
            {/* Which guide edition produced these numbers. Grouped with the
                controls on the right, as in the guide, so the left of the bar
                stays purely the editable identity of this scenario. */}
            <span className={styles.guideStamp}>
              {reference_.guideLabel} &middot; Term {reference_.guideTerm}
            </span>
            {isEvaluating && (
              <span className={styles.savingIndicator}>
                <Spinner size={14} /> Evaluating…
              </span>
            )}
            <Button size="sm" onClick={resetToZero}>
              Reset to zero
            </Button>
            <Badge tone={record.status === 'finalized' ? 'ok' : 'warn'} dot>
              {record.status === 'finalized' ? 'Finalized' : 'Draft'}
            </Badge>
            <Button
              size="sm"
              onClick={() => { updateMeta({ status: record.status === 'finalized' ? 'draft' : 'finalized' }); }}
            >
              {record.status === 'finalized' ? 'Reopen' : 'Finalize'}
            </Button>
          </div>
        }
      />

      <div className={styles.body}>
        {/* The guide's own framing, and the single most important thing to get
            wrong: these are fees Microsoft pays Exigo, not customer discounts. */}
        <p className={styles.lede}>
          Partner earnings from Microsoft Commerce Incentives. These are fees Microsoft pays Exigo
          for delivering engagements — not discounts, rebates or funding for the customer.{' '}
          <b>Never quote them to a customer.</b>
        </p>

        <DealContextCard input={input} setInput={setInput} reference={reference.data} />

        <CapabilityPanel input={input} setInput={setInput} reference={reference.data} />

        <AreaTabs
          value={input.area}
          onChange={(area) => { setInput((p) => ({ ...p, area })); }}
          reference={reference.data}
        />

        {/* CSP Promotions replaces the deal cards and the results entirely: it
            prices customer discounts rather than sizing a nomination, so a deal
            value strip and an eligible list would both be meaningless here. */}
        {isPromos ? (
          <PromotionsCard
            reference={reference.data}
            value={record.promoInput}
            onChange={(next) => { updateMeta({ promoInput: next }); }}
            fx={input.fx}
            catalogue={record.promoCatalogue}
            onCatalogueChange={(next) => { updateMeta({ promoCatalogue: next }); }}
          />
        ) : (
          <>
            {basesInArea.includes('acr') && (
              <AcrCard input={input} setInput={setInput} reference={reference.data} result={result} />
            )}

            <SeatsAcvCard input={input} setInput={setInput} basesInArea={basesInArea} />

            {(input.area === 'abs' || input.area === 'security') && (
              <PrequalCard input={input} setInput={setInput} />
            )}

            {evaluateError && (
              <div className={styles.errorBanner}>
                Could not evaluate this scenario: {evaluateError.message}
              </div>
            )}

            {result?.warnings.map((w) => (
              <div className={styles.warningBanner} key={w.kind}>
                {w.message}
              </div>
            ))}

            {result && (
              <DealValueStrip result={result} fx={input.fx} termMonths={input.termMonths} />
            )}

            {result && (
              <ResultsList
                result={result}
                fx={input.fx}
                anyInput={anyInput}
                input={input}
                areaName={
                  reference_.areas.find((a) => a.key === input.area)?.name ?? input.area
                }
                customerName={record.customerName}
                guideLabel={reference_.guideLabel}
              />
            )}
          </>
        )}

        <ClaimConstraints />

        <GuideFooter guideLabel={reference_.guideLabel} guideTerm={reference_.guideTerm} />
      </div>
    </>
  );
}

function BackIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
